const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const desktop = matchMedia('(min-width: 1024px)');

class KoreScienceGuide extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.tablist = this.querySelector('.kore-science-tabs');
    if (!this.tabs.length || !this.tablist) return;
    this.tablist.hidden = false;
    this.tablist.setAttribute('role', 'tablist');
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.addEventListener('click', () => this.activate(index), { signal });
      tab.addEventListener('keydown', event => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.activate(next);
        this.tabs[next].focus({ preventScroll: true });
      }, { signal });
    });
    this.panels.forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0; });
    document.addEventListener('shopify:block:select', event => {
      const panel = event.target.closest?.('[data-panel]');
      if (!panel || !this.contains(panel)) return;
      const index = this.tabs.findIndex(tab => tab.dataset.tab === panel.dataset.panel);
      if (index >= 0) this.activate(index);
      const disclosure = event.target.closest('details');
      if (disclosure) disclosure.open = true;
    }, { signal });
    reduced.addEventListener('change', () => { this.animation?.cancel(); this.imageAnimation?.cancel(); }, { signal });
    this.dataset.ready = 'true';
    this.activate(0, false);
  }
  activate(index, animate = true) {
    this.animation?.cancel();
    this.imageAnimation?.cancel();
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    const key = this.tabs[index].dataset.tab;
    this.panels.forEach(panel => { panel.hidden = panel.dataset.panel !== key; });
    this.dataset.active = key;
    const panel = this.panels.find(panel => !panel.hidden);
    if (panel && animate && !reduced.matches && this.closest('kore-chapter')?.dataset.motion === 'true') {
      this.animation = panel.animate([{ opacity: .25, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
      const visual = panel.querySelector('.kore-science-visual');
      if (visual) this.imageAnimation = visual.animate([{ opacity:.2, clipPath:'inset(6% 0 28% 0)', transform:'translateY(14px)' },{ opacity:1, clipPath:'inset(0% 0 0% 0)', transform:'translateY(0)' }], { duration:900, easing:'cubic-bezier(.22,1,.36,1)' });
    }
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.animation?.cancel();
    this.imageAnimation?.cancel();
  }
}

class KoreMobileDrawer extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.dialog = this.querySelector('dialog');
    this.details = this.querySelector('details');
    this.trigger = this.querySelector('summary');
    this.closeButton = this.querySelector('.kore-drawer-close');
    if (!this.dialog?.showModal || !this.trigger) return;
    this.details.open = false;
    if (this.dialog.open) this.dialog.close();
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.dataset.ready = 'true';
    this.trigger.addEventListener('click', event => { event.preventDefault(); this.open(); }, { signal });
    this.closeButton.addEventListener('click', () => this.close(), { signal });
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal });
    this.dialog.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (link) { event.preventDefault(); this.close(link.href); return; }
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.releaseScroll();
      this.trigger.setAttribute('aria-expanded', 'false');
    }, { signal });
    desktop.addEventListener('change', () => { if (desktop.matches) this.finishClose(false); }, { signal });
    reduced.addEventListener('change', () => this.animation?.finish(), { signal });
  }
  lockScroll() {
    if (this.savedScroll) return;
    this.savedScroll = { x: scrollX, y: scrollY };
    this.bodyStyles = {};
    for (const name of ['position', 'top', 'left', 'width', 'overflow']) this.bodyStyles[name] = document.body.style[name];
    this.htmlOverflow = document.documentElement.style.overflow;
    document.body.style.position = 'fixed';
    document.body.style.top = `${-this.savedScroll.y}px`;
    document.body.style.left = `${-this.savedScroll.x}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.setAttribute('data-kore-drawer-open', '');
  }
  releaseScroll() {
    if (!this.savedScroll) return;
    const saved = this.savedScroll;
    this.savedScroll = null;
    Object.entries(this.bodyStyles).forEach(([key, value]) => { document.body.style[key] = value; });
    document.documentElement.style.overflow = this.htmlOverflow;
    document.documentElement.removeAttribute('data-kore-drawer-open');
    scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  }
  open() {
    if (desktop.matches || this.dialog.open) return;
    this.closeToken = Symbol('open');
    this.animation?.cancel();
    this.lockScroll();
    try { this.dialog.showModal(); }
    catch { this.releaseScroll(); this.details.open = true; return; }
    this.trigger.setAttribute('aria-expanded', 'true');
    this.closeButton.focus({ preventScroll: true });
    if (!reduced.matches) this.animation = this.dialog.animate([{ transform: 'translateX(100%)', opacity: .5 }, { transform: 'translateX(0)', opacity: 1 }], { duration: 430, easing: 'cubic-bezier(.22,1,.36,1)' });
  }
  async close(href) {
    if (!this.dialog.open) return;
    const token = this.closeToken = Symbol('close');
    this.animation?.cancel();
    if (!reduced.matches) {
      this.animation = this.dialog.animate([{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(100%)', opacity: .5 }], { duration: 230, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
      try { await this.animation.finished; } catch { /* A newer action owns the drawer. */ }
    }
    if (token !== this.closeToken || !this.isConnected) return;
    this.finishClose(!href);
    if (href) {
      const url = new URL(href, location.href);
      const target = url.hash && document.getElementById(decodeURIComponent(url.hash.slice(1)));
      const sameView = url.searchParams.get('view') === new URL(location.href).searchParams.get('view');
      if (url.origin === location.origin && url.pathname === location.pathname && sameView && target) {
        history.pushState(null, '', url.href);
        target.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
        // Announce the destination without leaving keyboard focus in a closed dialog.
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      } else location.assign(url.href);
    }
  }
  finishClose(returnFocus = true) {
    this.closeToken = null;
    this.animation?.cancel();
    if (this.dialog?.open) this.dialog.close();
    this.releaseScroll();
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (returnFocus && this.isConnected && this.trigger?.getClientRects().length) this.trigger.focus({ preventScroll: true });
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.finishClose(false);
  }
}
if (!customElements.get('kore-science-guide')) customElements.define('kore-science-guide', KoreScienceGuide);
if (!customElements.get('kore-mobile-drawer')) customElements.define('kore-mobile-drawer', KoreMobileDrawer);
